import { Settings } from '@streamr/tv-engine';
import { EventEmitter } from 'eventemitter3';
import { AudioDecoderWrapper } from './AudioDecoderWrapper';
import * as audioPlayerProcessor from './workers/audioplayer.worklet';
export class AudioPlayer extends EventEmitter {
    audioDecoderWrapper;
    audioContext;
    audioWorkletNode;
    optimalDelayMillis = 0;
    constructor() {
        super();
        this.audioContext = new AudioContext({
            sampleRate: Settings.audioSampleRate,
        });
        this.audioContext.addEventListener('statechange', () => {
            if (this.audioContext.state === 'suspended') {
                this.onAudioContextSuspendedOrInterrupted();
            }
            if (this.audioContext
                .state ===
                'interrupted') {
                this.onAudioContextSuspendedOrInterrupted();
            }
            if (this.audioContext.state === 'running') {
                this.onAudioContextRunning();
            }
        });
        this.audioDecoderWrapper = new AudioDecoderWrapper();
        this.audioDecoderWrapper.on('audioData', (audioData) => {
            this.audioWorkletNode?.port.postMessage({
                type: 'feedAudioFrame',
                decodedAudioData: audioData,
                optimalDelayMillis: this.optimalDelayMillis,
            }, audioData.channels.map((channel) => channel.buffer));
        });
    }
    onAudioContextRunning() {
        // now it is time to start the ringbuffer
        this.emit('audioContextRunning');
    }
    onAudioContextSuspendedOrInterrupted() {
        // now it is time to clear the ringbuffer
        this.emit('audioContextStopped');
    }
    async init() {
        await this.audioDecoderWrapper.init();
        await this.audioContext.audioWorklet.addModule(
        // biome-ignore lint/suspicious/noExplicitAny: @todo fix types (the "any")
        audioPlayerProcessor);
        // Create and configure worklet
        this.audioWorkletNode = new AudioWorkletNode(this.audioContext, 'audio-player');
        this.audioWorkletNode.connect(this.audioContext.destination);
        this.audioWorkletNode.port.onmessage = (e) => {
            if (e.data.type === 'purged') {
                // The 'purged' message should not be removed even though it is not currently used.
                // It is used for moving the AudioData from the worklet to the main thread, where it can be garbage-collected.
            }
            if (e.data.type === 'played') {
                const presentationTimeMicros = Number(e.data.frame.timestamp) +
                    Number(e.data.frame.duration);
                this.emit('newReferenceTimes', presentationTimeMicros, e.data.realTimeMillis);
            }
            if (e.data.type === 'resetCompleted') {
                this.emit('workletResetCompleted');
            }
        };
    }
    startNowFromPresentationTimeMicros(presentationTimeMicros) {
        this.audioWorkletNode?.port.postMessage({
            type: 'startNowFromPresentationTimeMicros',
            presentationTimeMicros,
        });
    }
    // This will start audio playback optimalDelayMillis
    // from the end of the audio buffer, if there is enough data
    // in the buffer. If there is not enough data, it will start
    // buffering the audio and then start playback when enough data
    // is available.
    startAtOptimalDelayMillis(optimalDelayMillis) {
        this.audioWorkletNode?.port.postMessage({
            type: 'startAtOptimalDelayMillis',
            optimalDelayMillis,
        });
    }
    setOptimalDelayMillis(optimalDelayMillis) {
        this.optimalDelayMillis = optimalDelayMillis;
    }
    feedAudioFrames(audioFrames) {
        this.audioDecoderWrapper.decode(audioFrames);
    }
    reset() {
        return new Promise((resolve) => {
            this.once('workletResetCompleted', () => {
                resolve();
            });
            this.audioWorkletNode?.port.postMessage({
                type: 'reset',
            });
        });
    }
    async unmute() {
        // try resuming twice to handle the case where
        // the audio context is interrupted
        try {
            await this.audioContext.resume();
        }
        catch (_error) {
            await this.audioContext.resume();
        }
    }
    async mute() {
        await this.audioContext.suspend();
    }
    dispose() {
        this.audioContext.close();
    }
}
